// 権限マップ画面: プロファイル × 危険権限のヒートマップ → ユーザー一覧 → 付与経路ドロワー → CSV。
// 集計・絞り込み・CSV 生成は permission-map-data（純関数）が持ち、ここは描画と状態遷移だけを持つ。
//
// 正直さの要件（spec）:
// - 実効権限を確定できなかったユーザー（hasUnresolvedAssignment）を「権限なし」と描かない。
//   サマリ・行の注意列・ドロワー冒頭の 3 箇所で必ず名指しする。
// - 数えている範囲（critical/high）をそのまま名乗る。「危険権限」と総称すると、medium/low だけを
//   持つ人が 0 件と表示され「危険な権限は無い」と読めてしまう（ドロワー・CSV には全 9 種を出す）。
import { DANGEROUS_PERMISSIONS, type PermissionKey } from "@cloud-checker/shared";
import { createBadge } from "../ui/badge";
import { downloadCsv } from "../ui/csv";
import { createDataTable, type Column } from "../ui/data-table";
import { createDrawer } from "../ui/drawer";
import { createHeatmap } from "../ui/heatmap";
import { SEVERITY_ORDER } from "../ui/severity-badge";
import { TONE_CLASSES } from "../ui/tokens";
import { createSeverityBadge } from "./components/severity-badge";
import type {
  PermissionMapDto,
  PermissionMapUserDto,
  PermissionSetSummaryDto,
} from "./permission-map-api";
import {
  DANGER_COUNT_LABEL,
  DANGER_ONLY_LABEL,
  HEATMAP_COLUMN_LABELS,
  HOLDER_COUNT_LABEL,
  buildHeatmapRows,
  countUnresolved,
  countUnresolvedInPopulation,
  dangerCountOf,
  filterUsers,
  formatPermissionHolderCount,
  permissionMapToCsv,
  profileLabelOf,
  profileOfHeatmapRow,
  sortUsersByRisk,
  topPermissionSets,
  unusedPermissionSets,
  type HeatmapProfileRow,
  type PermissionMapFilter,
} from "./permission-map-data";

// 表示ラベル → PermissionKey の逆引き（ヒートマップのセルは列ラベルしか返さない）。
const PERMISSION_BY_LABEL = new Map<string, PermissionKey>(
  DANGEROUS_PERMISSIONS.map((p) => [p.label, p.key] as const),
);
const LABEL_BY_PERMISSION = new Map<PermissionKey, string>(
  DANGEROUS_PERMISSIONS.map((p) => [p.key, p.label] as const),
);

const SOURCE_LABEL: Record<"profile" | "permission-set", string> = {
  profile: "プロファイル",
  "permission-set": "権限セット",
};

// 行の注意列。「確定できない」を必ず含める（0 件バッジの隣で「権限なし」と読ませないため）。
const UNRESOLVED_ROW_LABEL = "権限を確定できない割当あり";

// PermissionSetSummaryDto.dangerCount は critical/high だけでなく監視対象 9 種すべてを数える
// （backend permission-map.ts）。ユーザー行の DANGER_COUNT_LABEL（重大/高）と同じ名前にすると
// 別々の母数が同じ見出しで並ぶため、こちらは数えた範囲を明示する。
const SET_PERMISSION_COUNT_LABEL = "要注意権限の数（監視対象 9 種）";

const TOP_PERMISSION_SETS = 10;

function chip(label: string, value: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700";
  el.textContent = label + " " + value;
  return el;
}

function heading(level: "h2" | "h3", text: string, className: string): HTMLElement {
  const h = document.createElement(level);
  h.className = className;
  h.textContent = text;
  return h;
}

function paragraph(text: string, className: string): HTMLElement {
  const p = document.createElement("p");
  p.className = className;
  p.textContent = text;
  return p;
}

export interface PermissionMapProps {
  readonly map: PermissionMapDto;
}

export function renderPermissionMap(root: HTMLElement, props: PermissionMapProps): void {
  root.replaceChildren();

  const users = props.map.users;
  const heatmapRows = buildHeatmapRows(users);
  const nonProfileSets = props.map.permissionSets.filter((s) => !s.isOwnedByProfile);
  const unused = unusedPermissionSets(props.map.permissionSets);

  let filter: PermissionMapFilter = {};

  // ---- サマリ帯 ----
  const summary = document.createElement("div");
  summary.dataset["testid"] = "permission-map-summary";
  summary.className = "mb-4 flex flex-wrap gap-3";
  summary.append(
    chip("対象ユーザー", users.length + " 名"),
    chip("重大/高の権限保有者", users.filter((u) => dangerCountOf(u) > 0).length + " 名"),
    // 未確定は行内注記だけにしない（既定ソートで画面外に落ちた行は誰にも見えないため）。
    chip("権限を確定できない", countUnresolved(users) + " 名"),
    chip("権限セット", nonProfileSets.length + " 件"),
    chip("未使用の権限セット", unused.length + " 件"),
  );

  const meta = paragraph(
    "取得日時: " + new Date(props.map.runAt).toLocaleString("ja-JP"),
    "mb-4 text-sm text-slate-500",
  );

  // ---- ヒートマップ ----
  const heatSection = document.createElement("section");
  heatSection.className = "mb-6";
  heatSection.append(
    heading("h2", "プロファイル別の重大/高の権限保有状況", "text-lg font-medium text-slate-900"),
    paragraph(
      "セルを選択すると、そのプロファイルでその権限を持つユーザーだけに一覧を絞り込みます。人数は実効権限を確定できたユーザーの数です。",
      "mt-1 mb-2 text-sm text-slate-600",
    ),
    createHeatmap({
      rows: heatmapRows,
      // 列は必ず固定リストを渡す（全員が未確定の行は cells が空なので、rows から導出すると列が消える）。
      columns: HEATMAP_COLUMN_LABELS,
      ariaLabel: "プロファイル別の重大/高の権限保有者数",
      rowHeader: "プロファイル",
      formatValue: formatPermissionHolderCount,
      emptyMessage: "対象ユーザーがいません。",
      onCellClick: (rowLabel, columnLabel) => {
        // 行見出しは表示ラベル。実値に直さないと「プロファイル不明」行（実値 ""）で 0 件になる。
        const profile = profileOfHeatmapRow(heatmapRows, rowLabel);
        const permission = PERMISSION_BY_LABEL.get(columnLabel);
        // どちらかを解決できないときは絞り込みを変えない（セルが指した中身と一覧が食い違うため）。
        if (profile === undefined || permission === undefined) return;
        filter = { profile, permission };
        syncControls();
        renderTable();
      },
    }),
  );

  // 人数セルは確定分の下限でしかない。行ごとの未確定人数を凡例として必ず添える。
  const unresolvedRows = heatmapRows.filter((r) => r.unresolvedCount > 0);
  if (unresolvedRows.length > 0) {
    const ul = document.createElement("ul");
    ul.className = "mt-2 list-disc pl-5 text-sm text-amber-800";
    for (const r of unresolvedRows) {
      const li = document.createElement("li");
      li.textContent = r.row + ": 権限を確定できない " + r.unresolvedCount + " 名（セルの人数には含まれません）";
      ul.append(li);
    }
    heatSection.append(ul);
  }

  // ---- 絞り込み ----
  const bar = document.createElement("div");
  bar.className = "mb-3 flex flex-wrap items-end gap-3";

  const searchWrap = document.createElement("div");
  const searchLabel = document.createElement("label");
  searchLabel.className = "block text-xs font-medium text-slate-600";
  searchLabel.htmlFor = "pm-search";
  searchLabel.textContent = "検索（氏名・ユーザー名）";
  const searchInput = document.createElement("input");
  searchInput.id = "pm-search";
  searchInput.type = "search";
  searchInput.className = "mt-1 rounded border border-slate-300 px-2 py-1 text-sm";
  searchInput.addEventListener("input", () => {
    // 空文字は「絞り込みなし」として filterUsers 側で無視されるのでキーを消す必要はない。
    filter = { ...filter, search: searchInput.value };
    renderTable();
  });
  searchWrap.append(searchLabel, searchInput);

  // プロファイルは "" も実在の値（読めなかったプロファイル）。「未指定」と同じ選択肢に潰さないため、
  // option の value には配列添字を使う（-1 = 未指定）。
  const profileValues = heatmapRows.map((r) => r.profile);
  const profileWrap = document.createElement("div");
  const profileLabel = document.createElement("label");
  profileLabel.className = "block text-xs font-medium text-slate-600";
  profileLabel.htmlFor = "pm-profile";
  profileLabel.textContent = "プロファイル";
  const profileSelect = document.createElement("select");
  profileSelect.id = "pm-profile";
  profileSelect.className = "mt-1 rounded border border-slate-300 px-2 py-1 text-sm";
  profileSelect.append(option("-1", "すべて"));
  heatmapRows.forEach((r, i) => profileSelect.append(option(String(i), r.row)));
  profileSelect.addEventListener("change", () => {
    const { profile: _omit, ...rest } = filter;
    const value = profileValues[Number(profileSelect.value)];
    filter = value === undefined ? rest : { ...rest, profile: value };
    renderTable();
  });
  profileWrap.append(profileLabel, profileSelect);

  const setValues = nonProfileSets.map((s) => s.label);
  const setWrap = document.createElement("div");
  const setLabel = document.createElement("label");
  setLabel.className = "block text-xs font-medium text-slate-600";
  setLabel.htmlFor = "pm-permission-set";
  setLabel.textContent = "権限セット";
  const setSelect = document.createElement("select");
  setSelect.id = "pm-permission-set";
  setSelect.className = "mt-1 rounded border border-slate-300 px-2 py-1 text-sm";
  setSelect.append(option("-1", "すべて"));
  setValues.forEach((label, i) => setSelect.append(option(String(i), label)));
  setSelect.addEventListener("change", () => {
    const { permissionSet: _omit, ...rest } = filter;
    const value = setValues[Number(setSelect.value)];
    filter = value === undefined ? rest : { ...rest, permissionSet: value };
    renderTable();
  });
  setWrap.append(setLabel, setSelect);

  const dangerWrap = document.createElement("div");
  dangerWrap.className = "flex items-center gap-2 pb-1";
  const dangerCheckbox = document.createElement("input");
  dangerCheckbox.id = "pm-danger-only";
  dangerCheckbox.type = "checkbox";
  dangerCheckbox.className = "h-4 w-4 rounded border-slate-300";
  dangerCheckbox.addEventListener("change", () => {
    filter = { ...filter, dangerOnly: dangerCheckbox.checked };
    renderTable();
  });
  const dangerLabel = document.createElement("label");
  dangerLabel.className = "text-sm text-slate-700";
  dangerLabel.htmlFor = "pm-danger-only";
  // 「危険権限のみ」と書かない: 数えているのは重大/高だけで、medium/low だけの保有者も残る。
  dangerLabel.textContent = DANGER_ONLY_LABEL;
  dangerWrap.append(dangerCheckbox, dangerLabel);

  // ヒートマップのセルが設定する permission 絞り込みには専用の入力が無いため、解除手段を必ず置く。
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className =
    "rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  resetBtn.textContent = "絞り込みを解除";
  resetBtn.addEventListener("click", () => {
    filter = {};
    syncControls();
    renderTable();
  });

  const csvBtn = document.createElement("button");
  csvBtn.type = "button";
  csvBtn.className =
    "rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  // 出すのは全ユーザー（絞り込みは反映しない）。ボタン自身に何件出るかを書く。
  csvBtn.textContent = "CSV 出力（全 " + users.length + " 名・絞り込みは反映しません）";
  csvBtn.addEventListener("click", () =>
    downloadCsv("salesforce-permission-map.csv", permissionMapToCsv(users)),
  );

  bar.append(searchWrap, profileWrap, setWrap, dangerWrap, resetBtn, csvBtn);

  // ---- ユーザー表 + ドロワー ----
  const drawer = createDrawer({ title: "実効権限" });
  const tableHost = document.createElement("div");

  const columns: Column<PermissionMapUserDto>[] = [
    { key: "name", header: "氏名", cell: (u) => u.name },
    { key: "username", header: "ユーザー名", cell: (u) => u.username },
    // "" を空セルにすると「値が無い」のか「読めなかった」のか区別できないため、必ずラベルを出す。
    { key: "profile", header: "プロファイル", cell: (u) => profileLabelOf(heatmapRows, u.profile) },
    {
      key: "danger",
      header: DANGER_COUNT_LABEL,
      cell: (u) => {
        const n = dangerCountOf(u);
        return createBadge({ label: String(n), tone: n > 0 ? "danger" : "neutral" });
      },
    },
    { key: "sets", header: "権限セット数", cell: (u) => String(u.permissionSetLabels.length) },
    {
      key: "unresolved",
      header: "注意",
      cell: (u) =>
        u.hasUnresolvedAssignment ? createBadge({ label: UNRESOLVED_ROW_LABEL, tone: "warning" }) : "—",
    },
  ];

  function renderTable(): void {
    const visible = sortUsersByRisk(filterUsers(users, filter));
    const count = paragraph(visible.length + " 名", "mb-2 text-sm text-slate-500");

    const nodes: HTMLElement[] = [count];
    const permission = filter.permission;
    if (permission !== undefined) {
      // permission で絞ると未確定ユーザーは必ず落ちる。母集団の未確定人数を併記しないと、
      // セル押下後の一覧が「この権限を持つのはこの N 名だけ」と読めてしまう。
      const label = LABEL_BY_PERMISSION.get(permission) ?? permission;
      const populationUnresolved = countUnresolvedInPopulation(users, filter);
      nodes.push(
        paragraph(
          "「" +
            label +
            "」の保有者に絞り込み中です（実効権限を確定できたユーザーのみ）。同じ母集団に権限を確定できない " +
            populationUnresolved +
            " 名がいます。",
          "mb-2 rounded border px-3 py-2 text-sm " + TONE_CLASSES.warning.bar,
        ),
      );
    }

    nodes.push(
      createDataTable<PermissionMapUserDto>({
        caption: "Salesforce ユーザーの実効権限一覧",
        columns,
        rows: visible,
        onRowClick: openDetail,
        emptyMessage: "該当するユーザーはいません。",
        rowTestId: "permission-user-row",
      }),
    );
    tableHost.replaceChildren(...nodes);
  }

  function syncControls(): void {
    searchInput.value = filter.search ?? "";
    const profileIndex = filter.profile === undefined ? -1 : profileValues.indexOf(filter.profile);
    profileSelect.value = String(profileIndex);
    const setIndex = filter.permissionSet === undefined ? -1 : setValues.indexOf(filter.permissionSet);
    setSelect.value = String(setIndex);
    dangerCheckbox.checked = filter.dangerOnly === true;
  }

  function openDetail(user: PermissionMapUserDto): void {
    drawer.setTitle(user.name);
    drawer.setContent(createUserDetail(user, heatmapRows));
    drawer.open();
  }

  renderTable();

  // ---- 権限セット別 ----
  const setSection = document.createElement("section");
  setSection.className = "mt-8";
  setSection.append(
    heading("h2", "権限セット別の割当状況", "text-lg font-medium text-slate-900"),
    paragraph(
      "割当数は割当行から数えるため、無効化済みユーザーや期限切れの割当も含みます（実際の利用者数より多く出ます）。",
      "mt-1 mb-2 text-sm text-slate-600",
    ),
    createDataTable<PermissionSetSummaryDto>({
      caption: "権限セット別の割当数（上位 " + TOP_PERMISSION_SETS + " 件）",
      columns: [
        { key: "label", header: "権限セット", cell: (s) => s.label },
        { key: "holders", header: HOLDER_COUNT_LABEL, cell: (s) => String(s.holderCount) },
        { key: "danger", header: SET_PERMISSION_COUNT_LABEL, cell: (s) => String(s.dangerCount) },
      ],
      rows: topPermissionSets(props.map.permissionSets, TOP_PERMISSION_SETS),
      emptyMessage: "権限セットはありません。",
      rowTestId: "permission-set-row",
    }),
  );

  const unusedSection = document.createElement("section");
  unusedSection.className = "mt-6";
  unusedSection.append(heading("h2", "未使用の権限セット", "text-lg font-medium text-slate-900"));
  if (unused.length === 0) {
    unusedSection.append(paragraph("未使用の権限セットはありません。", "mt-1 text-sm text-slate-600"));
  } else {
    unusedSection.append(
      paragraph("割当が 1 件も無い権限セットです。棚卸しの候補になります。", "mt-1 text-sm text-slate-600"),
    );
    const ul = document.createElement("ul");
    ul.className = "mt-2 list-disc pl-5 text-sm text-slate-700";
    for (const s of unused) {
      const li = document.createElement("li");
      li.textContent = s.label;
      ul.append(li);
    }
    unusedSection.append(ul);
  }

  root.append(summary, meta, heatSection, bar, tableHost, setSection, unusedSection, drawer.element);
}

function option(value: string, label: string): HTMLOptionElement {
  const opt = document.createElement("option");
  opt.value = value;
  opt.textContent = label;
  return opt;
}

// ドロワー本体: 実効権限を経路付きで並べ、未確定なら冒頭で明示する。
function createUserDetail(
  user: PermissionMapUserDto,
  heatmapRows: readonly HeatmapProfileRow[],
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "space-y-4";

  if (user.hasUnresolvedAssignment) {
    wrap.append(
      paragraph(
        "割当先のプロファイルまたは権限セットを特定できず、このユーザーの実効権限を確定できていません。以下は確定できた分だけで、権限を持たないことを意味しません。",
        "rounded border px-3 py-2 text-sm " + TONE_CLASSES.warning.bar,
      ),
    );
  }

  const basics = document.createElement("div");
  basics.className = "space-y-1";
  basics.append(
    paragraph("ユーザー名: " + user.username, "text-sm text-slate-700"),
    paragraph("プロファイル: " + profileLabelOf(heatmapRows, user.profile), "text-sm text-slate-700"),
  );
  wrap.append(basics);

  const grantSection = document.createElement("section");
  grantSection.append(heading("h3", "実効権限と付与経路", "text-sm font-medium text-slate-700"));
  if (user.grants.length === 0) {
    grantSection.append(
      paragraph(
        user.hasUnresolvedAssignment
          ? "確定できた付与はありません。"
          : "監視対象の 9 種の権限はいずれも付与されていません。",
        "mt-1 text-sm text-slate-700",
      ),
    );
  } else {
    const ul = document.createElement("ul");
    ul.className = "mt-1 space-y-1 text-sm text-slate-700";
    const grants = [...user.grants].sort(
      (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] || a.label.localeCompare(b.label, "ja"),
    );
    for (const g of grants) {
      const li = document.createElement("li");
      li.className = "flex flex-wrap items-center gap-2";
      const text = document.createElement("span");
      text.textContent = g.label + " ← " + g.sourceLabel + "（" + SOURCE_LABEL[g.source] + "）";
      li.append(createSeverityBadge(g.severity), text);
      ul.append(li);
    }
    grantSection.append(ul);
  }
  wrap.append(grantSection);

  const setSection = document.createElement("section");
  setSection.append(heading("h3", "割当権限セット", "text-sm font-medium text-slate-700"));
  if (user.permissionSetLabels.length === 0) {
    setSection.append(paragraph("割当はありません。", "mt-1 text-sm text-slate-700"));
  } else {
    const ul = document.createElement("ul");
    ul.className = "mt-1 list-disc pl-5 text-sm text-slate-700";
    for (const label of user.permissionSetLabels) {
      const li = document.createElement("li");
      li.textContent = label;
      ul.append(li);
    }
    setSection.append(ul);
  }
  wrap.append(setSection);

  return wrap;
}
